import React, {useEffect, useState} from 'react';
import {Badge, Card, CardBody, Col, Row, Spinner} from 'reactstrap';
import moment from 'moment';

import {ClassroomIndividualAPI} from "api";

/**
 * PRESENZE DELLA CLASSE INDIVIDUALE.
 *
 * Fino a ieri per le lezioni individuali non esisteva alcuna presenza: l'unico segno che una
 * lezione fosse stata svolta era lo stato COMPLETED, che pero' lo scriveva il check-in stesso — e
 * per questo il corsista non riusciva a rientrare. Ora il backend annota `checkin_at` al primo
 * ingresso, e questa e' la finestra su quel dato.
 *
 * SI MOSTRANO SOLO LE LEZIONI GIA' PASSATE (il filtro e' del backend, come per le classi di
 * gruppo). Una lezione di domani non e' un'assenza: segnarla in rosso allarmerebbe per niente.
 *
 * Non e' visibile al corsista: la rotta ammette ADMIN, TEACHER e CONSULENT.
 */
const AttendancesInside = ({classroomId}) => {

    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(false);
    const [dati, setDati] = useState(null);

    useEffect(() => {
        let vivo = true;

        setCaricamento(true);
        setErrore(false);

        ClassroomIndividualAPI.detailAttendancesIndividual(classroomId).then((risposta) => {
            if (!vivo) return;              // scheda cambiata prima della risposta
            setDati(risposta);
            setCaricamento(false);
        }).catch((e) => {
            if (!vivo) return;
            console.error('presenze individuali:', e);
            setErrore(true);
            setCaricamento(false);
        });

        return () => { vivo = false; };
    }, [classroomId]);

    if (caricamento)
        return (
            <Card>
                <CardBody className="text-center py-5">
                    <Spinner color="primary"/>
                </CardBody>
            </Card>
        );

    if (errore)
        return (
            <Card>
                <CardBody className="text-center py-5">
                    <i className="ri-error-warning-line display-6 text-muted"/>
                    <p className="text-muted mt-2 mb-0">Non è stato possibile recuperare le presenze.</p>
                </CardBody>
            </Card>
        );

    const presenze = (dati && dati.attendances) ? dati.attendances : [];

    // Nessuna lezione ancora passata: e' il caso piu' comune su una classe appena partita, e va
    // detto a parole. Una lista vuota senza spiegazione sembra un errore.
    if (presenze.length === 0)
        return (
            <Card>
                <CardBody className="text-center py-5">
                    <i className="ri-calendar-check-line display-6 text-muted"/>
                    <p className="text-muted mt-2 mb-0">
                        Nessuna lezione ancora svolta: le presenze compaiono qui dopo la prima lezione.
                    </p>
                </CardBody>
            </Card>
        );

    const presenti = dati.present || 0;
    const totale = dati.total || presenze.length;
    const assenti = totale - presenti;

    return (
        <Card>
            <CardBody>
                <Row className="mb-4">
                    <Col xs={12}>
                        <h5 className="card-title mb-1">
                            Presenze
                            {(dati.student) ? <span className="text-muted fw-normal">
                                {" — " + dati.student.firstname + " " + dati.student.lastname}
                            </span> : null}
                        </h5>
                        <p className="text-muted mb-0">
                            Presente a <b>{presenti}</b> {(totale === 1) ? "lezione" : "lezioni"} su <b>{totale}</b> svolte
                            {(assenti > 0) ? <span className="text-danger">{" (" + assenti + (assenti === 1 ? " assenza" : " assenze") + ")"}</span> : null}
                        </p>
                    </Col>
                </Row>

                <div className="acitivity-timeline py-1">
                    {presenze.map((p, i) => (
                        <div className="acitivity-item py-3 d-flex" key={p.id || i}>
                            <div className="flex-shrink-0 avatar-xs acitivity-avatar">
                                <div className={"avatar-title rounded-circle " + (p.check ? "bg-soft-success text-success" : "bg-soft-danger text-danger")}>
                                    <i className={p.check ? "mdi mdi-check" : "mdi mdi-close"}/>
                                </div>
                            </div>
                            <div className="flex-grow-1 ms-3">
                                <h6 className="mb-1"><b>{moment(p.date_hour).format("ddd DD MMM YYYY")}</b></h6>
                                <p className="mb-1"><b>ore {moment(p.date_hour).format("HH:mm")}</b></p>
                                {(p.check)
                                    ? <React.Fragment>
                                        <Badge color={"success"}>Presente</Badge>
                                        {/* L'orario del primo ingresso dice anche se e' arrivato in ritardo.
                                            SI MOSTRA SOLO SE E' UN DATO VERO: le lezioni svolte prima che
                                            esistesse `checkin_at` sono state recuperate dalla migrazione usando
                                            l'orario di INIZIO, quindi per quelle i due valori coincidono al
                                            secondo e l'ora d'ingresso non la sappiamo. Scriverla lo stesso
                                            farebbe passare per misura una supposizione. */}
                                        {(p.checkin_at && !moment(p.checkin_at).isSame(moment(p.date_hour)))
                                            ? <span className="text-muted ms-2">
                                                entrato alle {moment(p.checkin_at).format("HH:mm")}
                                            </span>
                                            : null}
                                    </React.Fragment>
                                    : <Badge color={"danger"}>Assente</Badge>}
                            </div>
                        </div>
                    ))}
                </div>
            </CardBody>
        </Card>
    );
};

export default AttendancesInside;
